import { Link } from 'lucide-react';
import React from 'react';
import NodeProfileEditForm from './NodeProfileEditForm';

export type ProfileData = {
    id: string;
    type: 'person' | 'group' | 'entity' | 'effort';
    name: string;
    subtitle?: string;
    description?: string;
    tags: string[];
    avatars: string[]; // At least one avatar
    lastUpdated?: string;
    emails: string[];
    phoneNumbers: string[];
    socialMediaLinks: string[];
};

interface ProfileCardProps {
    data: ProfileData | null;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ data }) => {
    const [isEditing, setIsEditing] = React.useState(false);

    if (!data) {
        return (
            <div className="card card-side bg-base-100 shadow-xl w-full max-w-5xl mx-auto my-6">
                <div className="p-4 flex items-center">
                    <div className="avatar skeleton w-24 rounded-full">
                        <div className="w-24 rounded-full">

                        </div>
                    </div>
                </div>
                <div className="card-body p-4 m-4 skeleton">

                </div>

            </div>)
    }

    if (isEditing) {
        return (
            <div className="card card-side bg-base-100 shadow-xl w-full max-w-5xl mx-auto my-6">
                <div className="card-body">
                    <NodeProfileEditForm
                        initialData={{
                            id: data.id,
                            name: data.name,
                            subtitle: data.subtitle,
                            description: data.description,
                            tags: data.tags,
                            tagsV2: data.tags.map((tag) => ({ value: tag, label: tag })),
                        }}
                        rawData={data}
                        onSubmit={(updatedData) => {
                            console.log('Updated:', updatedData);
                            setIsEditing(false);
                        }}
                    />
                </div>
            </div>
        );
    }

    const {
        name,
        subtitle,
        description,
        tags,
        avatars,
        lastUpdated,
        emails,
        phoneNumbers,
        socialMediaLinks,
    } = data;


    return (
        <div className="card card-side bg-base-100 shadow-xl w-full max-w-5xl mx-auto my-6">
            {/* Avatar Section */}
            <div className="p-4 flex items-center">
                {avatars.length === 0 && (
                    <div className="avatar placeholder">
                        <div className="text-4xl bg-neutral text-neutral-content w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-1">
                            <span> {data.name[0].toUpperCase()}</span>
                        </div>
                    </div>
                )}

                {avatars.length > 0 && (
                    <div className="avatar">
                        <div className="w-24 rounded-full ring ring-primary ring-offset-base-100 ring-offset-1">
                            <img src={avatars[0]} alt={`${name}'s avatar`} />
                        </div>
                    </div>
                )}


            </div>

            {/* Main Profile Info */}
            <div className="card-body p-4">
                <h2 className="card-title text-2xl font-bold">{name}</h2>
                {subtitle && <p className="text-base text-gray-500">{subtitle}</p>}
                {description && <p className="mt-2">{description}</p>}

                {/* Tags */}
                {tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {tags.map((tag) => (
                            <div key={tag} className="badge badge-primary">
                                {tag}
                            </div>
                        ))}
                    </div>
                )}

                {/* Last Updated */}
                {lastUpdated && (
                    <p className="text-sm text-gray-400 mt-2">
                        Last updated: {lastUpdated}
                    </p>
                )}

                <button
                    onClick={() => setIsEditing(true)}
                    className="btn btn-secondary mt-4"
                >
                    Edit
                </button>
            </div>

            {/* Contact Details Section */}
            <div className="flex flex-col justify-center p-4 gap-2">
                {/* Emails */}
                {emails.length > 0 && (
                    <div>
                        <h3 className="font-semibold">Email</h3>
                        <ul className="list-disc ml-4">
                            {emails.map((email) => (
                                <li key={email} className="text-blue-600 hover:underline">
                                    <a href={`mailto:${email}`}>{email}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Phone Numbers */}
                {phoneNumbers.length > 0 && (
                    <div>
                        <h3 className="font-semibold">Phone</h3>
                        <ul className="list-disc ml-4">
                            {phoneNumbers.map((number) => (
                                <li key={number}>{number}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Social Media Links */}
                {socialMediaLinks.length > 0 && (
                    <div>
                        <h3 className="font-semibold">Social</h3>
                        <ul className="flex flex-wrap gap-3 mt-1">
                            {socialMediaLinks.map((link) => (
                                <li key={link}>
                                    <a
                                        href={link}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 hover:underline"
                                    >
                                        {new URL(link).hostname.replace('www.', '')}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileCard;
